import VueWrapper from '@/components/core/Vue/vue.wrapper';
import BaseFormComponent from '@/components/vuetify/Form/base-form';
import {CableCoreModel, CableModel, CreateEditCableCoreViewModel} from '@/sdk';
import {CableCoresApi} from '@/sdk/api-services';
import {Component, Prop, Watch} from 'vue-property-decorator';

@Component({
    name: 'CoreDetailsFormComponent',
})
export default class CoreDetailsFormComponent extends VueWrapper {
    @Prop({
        type: String,
        required: true,
    })
    public readonly selectedConnectionId!: string;

    @Prop({
        type: Object,
        default: null,
    })
    public readonly selectedCore!: CableCoreModel | null;

    @Prop({
        type: Array,
        default: () => [],
    })
    public readonly availableCables!: CableModel[];

    @Prop({
        type: Boolean,
        default: false,
    })
    public readonly readonly!: boolean;

    public $refs!: {
        coreFormRef: BaseFormComponent;
    };

    public cableCoresApi: CableCoresApi = new CableCoresApi();
    public isLoading: boolean = false;

    public coreTypes: string[] = ['Core', 'Screen', 'Armour', 'Braid', 'etc'];

    public coreFunctions: string[] = ['Power line', 'Signal line', 'Earth line', 'Bonding line', 'Multiple function'];

    public coreForm: CreateEditCableCoreViewModel = new CreateEditCableCoreViewModel({
        title: '',
        locationConnectionId: '',
        cableId: null,
        equipmentInPinId: null,
        equipmentOutPinId: null,
    });

    public get isEditing(): boolean {
        return !!this.selectedCore && !!this.selectedCore.id;
    }

    public mounted(): void {
        this.loadCoreData();
    }

    @Watch('selectedCore', {deep: true})
    public onSelectedCoreChanged(): void {
        this.loadCoreData();
    }

    public getCableTitle(cableId: string): string {
        const cable = this.availableCables.find(c => c.id === cableId);
        return cable?.title || `Cable ${cableId}`;
    }

    private loadCoreData(): void {
        if (this.selectedCore) {
            this.coreForm = new CreateEditCableCoreViewModel({
                id: this.selectedCore.id,
                title: this.selectedCore.title || '',
                locationConnectionId: this.selectedCore.locationConnectionId,
                cableId: this.selectedCore.cableId || null,
                equipmentInPinId: this.selectedCore.equipmentInPinId || null,
                equipmentOutPinId: this.selectedCore.equipmentOutPinId || null,

                coreType: this.selectedCore.coreType || null,
                coreRatedCurrent: this.selectedCore.coreRatedCurrent || null,
                coreCrossSection: this.selectedCore.coreCrossSection || null,
                coreIntrinsicSafety: this.selectedCore.coreIntrinsicSafety || null,
                coreMaterial: this.selectedCore.coreMaterial || null,
                coreFunction: this.selectedCore.coreFunction || null,
            });
        } else {
            this.resetForm();
        }
    }

    public async saveCore(): Promise<void> {
        if (!this.$refs.coreFormRef.validate() || this.isLoading) {
            return;
        }

        try {
            this.isLoading = true;

            if (this.isEditing && this.selectedCore?.id) {
                // this.coreForm.coreRatedCurrent = this.coreForm.coreRatedCurrent != null ? Number(this.coreForm.coreRatedCurrent) : null;
                // this.coreForm.coreCrossSection = this.coreForm.coreCrossSection != null ? Number(this.coreForm.coreCrossSection) : null;
                // Update existing core
                await this.cableCoresApi.updateCableCore(this.selectedCore.id, this.coreForm);

                this.AlertSrv.show('success', 'Core updated successfully!');
                this.$emit('core-updated', this.selectedCore.id);
            } else {
                // Create new core
                let newCore: CableCoreModel;

                if (this.coreForm.cableId) {
                    // Create core with cable
                    const coreArray = await this.cableCoresApi.createCableCoresMultiple(this.coreForm.cableId, 0, [this.coreForm]);
                    newCore = coreArray[0];
                } else {
                    // Create core without cable (standalone core)
                    newCore = await this.cableCoresApi.createCoreWithoutCable(this.coreForm);
                }

                this.AlertSrv.show('success', 'Core created successfully!');
                this.$emit('core-created', newCore);
                this.resetForm();
            }
        } catch (error: any) {
            console.error('Error saving core:', error);
            this.AlertSrv.show('error', error?.message || 'Failed to save core. Please try again.');
        } finally {
            this.isLoading = false;
        }
    }

    public resetForm(): void {
        this.coreForm = new CreateEditCableCoreViewModel({
            title: '',
            locationConnectionId: this.selectedConnectionId,
            cableId: null,
            equipmentInPinId: null,
            equipmentOutPinId: null,

            coreType: null,
            coreRatedCurrent: null,
            coreCrossSection: null,
            coreIntrinsicSafety: null,
            coreMaterial: null,
            coreFunction: null,
        });

        this.$refs.coreFormRef?.reset();
        this.$emit('form-reset');
    }

    public validateForm(): boolean {
        return this.$refs.coreFormRef?.validate() || false;
    }
}
